import React, { useMemo } from 'react';

import { link } from '@aline/routes';
import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { DataGridPremium, gridClasses, useGridApiRef } from '@mui/x-data-grid-premium';
import { Link } from 'react-router-dom';
import {
  AlineButton,
  allLabelFilters,
  allOwnerFilters,
  ContentWrapper,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  OwnerCell,
  ReportFiltersPanel,
  reportRowHeightPx,
  Spacer,
  workflowListButtonId,
  WorkflowListLabelCell,
  WorkflowListStateCell,
} from 'src/components';
import { AlineTooltip } from 'src/components/generic';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { WorkflowIcon } from 'src/components/icons/WorkflowIcon';
import { Apollo } from 'src/gql/generated';
import { getIsBrowserHandledLinkClick, openLink, openLinkFromAnchorClick } from 'src/helpers';
import { getTemplatePublishedInTooltip } from 'src/helpers/integration-display-name';
import type { WorkflowFileSubset } from 'src/hooks';
import {
  createDataGridPremiumPaperStyles,
  FiltersAnchorProvider,
  useFiltersAnchorState,
  useWorkflowDataSource,
} from 'src/hooks';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { createClampStyles } from 'src/theme';
import type { LabelSubset, UserSubset, UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';

import { TemplateListToolbar } from './TemplateListToolbar';

const useStyles = makeStyles()(theme => ({
  content: {
    position: 'relative',
  },
  drawer: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  hoverEffectRow: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0, // Allow shrinking
  },
  xGrid: {
    flexGrow: 1,
    minHeight: 0, // Allow shrinking
    [`& .${gridClasses.row}`]: {
      [`&:hover #${workflowListButtonId}`]: {
        visibility: 'visible',
      },
    },
  },
}));

export interface TemplateListRow extends Pick<
  Apollo.Workflow,
  | 'id'
  | 'createdAt'
  | 'crmIntegrations'
  | 'name'
  | 'publishedAt'
  | 'status'
  | 'state'
  | '__typename'
  | 'updatedAt'
  | 'numTimesUsed'
> {
  files: WorkflowFileSubset[];
  labels: LabelSubset[];
  owner: UserSubset & UserWithOrg;
  parties: Pick<Apollo.Party, 'id' | 'name' | '__typename'>[];
  permissions: WorkflowPermissionSubset[];
}

export function TemplateList({
  templateType,
}: {
  templateType: 'published' | 'draft';
}): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiRef();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const workflowFilter = useMemo(
    () => ({
      published: templateType === 'published',
      variant: Apollo.WorkflowVariant.Template,
    }),
    [templateType]
  );
  const {
    dataSource,
    filterModelTuple: [filterModel, setFilterModel],
    onDataSourceError,
    sortModelTuple: [sortModel, setSortModel],
    totalCount,
  } = useWorkflowDataSource(workflowFilter, {
    apiRef,
    initialSortModel: [{ field: 'numTimesUsed', sort: 'desc' }],
  });
  const columnDefs = useMemo(
    () => [
      {
        field: 'name',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        filterable: true,
        flex: 1,
        headerName: 'Template',
        minWidth: 200,
        renderCell: TemplateListTitleCell,
        type: 'string' as const,
      },
      {
        field: 'state',
        filterable: false,
        flex: 0,
        headerName: 'Stage',
        minWidth: 120,
        renderCell: WorkflowListStateCell,
        sortable: false,
        type: 'string' as const,
      },
      {
        field: 'labels',
        filterOperators: allLabelFilters,
        filterable: true,
        flex: 0,
        headerName: 'Labels',
        minWidth: 300,
        renderCell: WorkflowListLabelCell,
        sortable: false,
        type: 'string' as const,
      },
      {
        field: 'owner',
        filterOperators: allOwnerFilters,
        filterable: true,
        flex: 0,
        headerName: 'Author',
        minWidth: 120,
        renderCell: OwnerCell,
        type: 'string' as const,
      },
      {
        align: 'left' as const,
        field: 'numTimesUsed',
        flex: 0,
        headerAlign: 'left' as const,
        headerName: '# of Times Used',
        minWidth: 170,
        type: 'number' as const,
      },
    ],
    []
  );

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <ContentWrapper className={classes.content}>
        <div className={classes.listContainer}>
          <DataGridPremium<TemplateListRow>
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            lazyLoading
            showToolbar
            apiRef={apiRef}
            className={classes.xGrid}
            columns={columnDefs}
            dataSource={dataSource}
            filterModel={filterModel}
            getEstimatedRowHeight={() => reportRowHeightPx}
            getRowHeight={() => 'auto'}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
              sorting: {
                sortModel: [
                  {
                    field: 'numTimesUsed',
                    sort: 'desc',
                  },
                ],
              },
            }}
            slotProps={{
              filterPanel: {
                filterableColumns: 'all',
              },
              footer: {
                noun: 'templates',
                totalCount,
              },
              panel: {
                placement: 'bottom-start',
                target: filtersAnchorEl ?? undefined,
              },
              toolbar: {
                workflowCount: totalCount,
              },
            }}
            slots={{
              baseTooltip: GridAlineTooltip,
              filterPanel: ReportFiltersPanel,
              footer: ListCountFooter,
              noRowsOverlay: TemplatesListEmpty,
              toolbar: TemplateListToolbar,
            }}
            sortModel={sortModel}
            sx={createDataGridPremiumPaperStyles}
            onDataSourceError={onDataSourceError}
            onFilterModelChange={setFilterModel}
            onRowClick={({ id, row }, event) => {
              /**
               * A modifier or middle click is a request for a new browser tab. The row background is
               * not a link, so honouring it here would navigate the current tab instead — do nothing
               * and let the row's name link be the thing that opens in a new tab.
               */
              if (getIsBrowserHandledLinkClick(event)) return;

              openLink(
                link.TemplateSelectedTemplateIndex({ workflowId: String(id) }),
                row.name || 'Untitled Template'
              );
            }}
            onSortModelChange={setSortModel}
          />
        </div>
      </ContentWrapper>
    </FiltersAnchorProvider>
  );
}

function TemplatesListEmpty(): JSX.Element {
  return (
    <ListEmpty
      icon={<WorkflowIcon fill="#9CA7AB" height={32} width={32} />}
      subtitle="Click new to create a template"
      title="No templates"
    />
  );
}

const useCellStyles = makeStyles()(theme => ({
  button: {
    visibility: 'hidden',
  },
  cell: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
    width: '100%',
  },
  nameLink: {
    minWidth: 0,
    textDecoration: 'none',
  },
  nameText: {
    ...createClampStyles(2),
    color: theme.appColors.iconsType,
  },
  publishedCrmIcon: {
    borderRadius: 4,
    display: 'block',
    flexShrink: 0,
    height: 16,
    width: 16,
  },
  publishedCrmIconWrapper: {
    alignItems: 'center',
    display: 'flex',
  },
}));

export function TemplateListTitleCell({
  row,
  value,
}: GridCellParams<TemplateListRow, TemplateListRow['name']>): JSX.Element {
  const { classes } = useCellStyles();
  const workflowName = value || 'Untitled';
  const isPublished = Boolean(row.publishedAt);
  const templateHref = link.TemplateSelectedTemplateIndex({ workflowId: row.id });

  return (
    <div className={classes.cell}>
      <Link
        className={classes.nameLink}
        to={templateHref}
        onClick={e => openLinkFromAnchorClick(e, templateHref, { title: workflowName })}
      >
        <Typography className={classes.nameText} variant="subtitle1">
          {workflowName}
        </Typography>
      </Link>
      {row.crmIntegrations.map(integration => (
        <AlineTooltip
          key={integration}
          arrow
          placement="top"
          title={getTemplatePublishedInTooltip(integration)}
        >
          <div className={classes.publishedCrmIconWrapper}>
            <CrmIntegrationIcon className={classes.publishedCrmIcon} integration={integration} />
          </div>
        </AlineTooltip>
      ))}
      <Spacer />
      {isPublished && (
        <AlineButton
          className={classes.button}
          color="primary"
          component={Link}
          id={workflowListButtonId}
          to={link.QuickStartLaunchTemplateSelectedTemplate({ templateWorkflowId: row.id })}
          variant="outlined"
          onClick={e => e.stopPropagation()}
        >
          Launch Workflow
        </AlineButton>
      )}
    </div>
  );
}
